import { useState, type FormEvent } from 'react';
import { signOut, toDataError, updateOwnPassword } from '../../lib/supabase';
import { useSession } from '../../lib/supabase/hooks';

interface PasswordRecoveryProps {
  /** Error returned in the redirect URL (e.g. expired link), if any. */
  readonly linkError: string | null;
}

/**
 * Landing screen for the "reset password" email link. Reuses the legacy login-card styles.
 * Supabase has already exchanged the link for a short-lived recovery session.
 */
export function PasswordRecovery({ linkError }: PasswordRecoveryProps) {
  const { session, loading } = useSession();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState<string | null>(linkError);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const goToLogin = async () => {
    await signOut().catch(() => undefined);
    window.location.replace('/');
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password.length < 8) {
      setMessage('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setMessage('The two passwords do not match.');
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await updateOwnPassword(password);
      setDone(true);
    } catch (err: unknown) {
      setMessage(toDataError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const linkInvalid = !loading && !session;

  return (
    <form className="login-card" onSubmit={onSubmit} autoComplete="on">
      <h1>LUVIA TOUR</h1>
      <div className="tag">Set a new password</div>

      {done ? (
        <>
          <p style={{ fontSize: 13, lineHeight: 1.7, margin: '12px 0' }}>
            Your password has been changed. Sign in with your email and the new password.
          </p>
          <button className="btn primary block" type="button" onClick={() => void goToLogin()}>
            Go to sign in
          </button>
        </>
      ) : linkInvalid ? (
        <>
          <p style={{ fontSize: 13, lineHeight: 1.7, margin: '12px 0' }}>
            This reset link is invalid or has expired. Ask your administrator to send a new one.
          </p>
          <button className="btn primary block" type="button" onClick={() => void goToLogin()}>
            Back to sign in
          </button>
        </>
      ) : (
        <>
          <input type="email" name="email" autoComplete="username" value={session?.user.email ?? ''} readOnly hidden />
          <div className="field">
            <label htmlFor="rp_new">New password</label>
            <input
              id="rp_new"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy || loading}
            />
          </div>
          <div className="field">
            <label htmlFor="rp_confirm">Confirm new password</label>
            <input
              id="rp_confirm"
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={busy || loading}
            />
          </div>
          <button className="btn primary block" type="submit" disabled={busy || loading}>
            {busy ? 'Saving…' : 'Save new password'}
          </button>
        </>
      )}

      <div className="msg">{message}</div>
    </form>
  );
}
